import { CheckDetails, ServiceType } from '../../types';
import { describeAssertion } from './AssertionsEditor';
import { formatDateTime } from '../../utils/datetime';

/**
 * What a check saw, laid out for "why is it down?". Fed by the ad-hoc test,
 * the service's last check, and the last failed check row.
 */
export function Diagnostics({ details, compact = false }: { details: CheckDetails; compact?: boolean }) {
  const t = details.timings;
  const tls = details.tls;
  return (
    <div className={`space-y-4 ${compact ? 'text-xs' : 'text-sm'}`}>
      {details.assertions && details.assertions.length > 0 && (
        <section>
          <h4 className="font-medium mb-1">Assertions</h4>
          <ul className="space-y-1">
            {details.assertions.map((r, i) => (
              <li key={i} className="flex items-start gap-2">
                <span
                  className={`mt-0.5 inline-block w-4 text-center font-bold ${r.passed ? 'text-status-up' : 'text-status-down'}`}
                  aria-label={r.passed ? 'passed' : 'failed'}
                >
                  {r.passed ? '✓' : '✗'}
                </span>
                <span className="min-w-0">
                  <span className="font-mono">{describeAssertion(r.assertion)}</span>
                  <span className="block text-gray-500 dark:text-gray-400">{r.message}</span>
                </span>
              </li>
            ))}
          </ul>
        </section>
      )}

      {(t || details.resolved_ip) && (
        <section>
          <h4 className="font-medium mb-1">Timing</h4>
          <dl className="grid grid-cols-3 sm:grid-cols-6 gap-2 font-mono tabular-nums">
            {details.resolved_ip && <Stat label="address" value={details.resolved_ip} />}
            {t && <Stat label="dns" value={ms(t.dns)} />}
            {t && <Stat label="connect" value={ms(t.connect)} />}
            {t && t.tls !== null && <Stat label="tls" value={ms(t.tls)} />}
            {t && t.ttfb !== null && <Stat label="first byte" value={ms(t.ttfb)} />}
            {t && <Stat label="total" value={ms(t.total)} />}
          </dl>
        </section>
      )}

      {tls && (
        <section>
          <h4 className="font-medium mb-1">Certificate</h4>
          <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1">
            <Row label="Subject" value={tls.subject ?? '—'} />
            <Row label="Issuer" value={tls.issuer ?? '—'} />
            <Row label="Expires" value={tls.valid_to ? `${formatDateTime(tls.valid_to)} (${daysWord(tls.days_remaining)})` : '—'} />
            <Row label="Chain" value={tls.authorized === null ? '—' : tls.authorized ? 'trusted' : `not trusted${tls.authorization_error ? ` — ${tls.authorization_error}` : ''}`} />
            {tls.alt_names.length > 0 && <Row label="Names" value={tls.alt_names.join(', ')} />}
          </dl>
        </section>
      )}

      {details.redirects && details.redirects.length > 0 && (
        <section>
          <h4 className="font-medium mb-1">Redirects</h4>
          <ol className="list-decimal list-inside font-mono break-all text-gray-600 dark:text-gray-300">
            {details.redirects.map((r, i) => <li key={i}>{r}</li>)}
          </ol>
        </section>
      )}

      {details.records && (
        <section>
          <h4 className="font-medium mb-1">Answer{details.resolver ? ` from ${details.resolver}` : ''}</h4>
          <ul className="font-mono">
            {details.records.length === 0 ? <li className="text-gray-500">(empty)</li> : details.records.map((r, i) => <li key={i}>{r}</li>)}
          </ul>
        </section>
      )}

      {details.ping && (
        <dl className="grid grid-cols-2 gap-2 font-mono tabular-nums">
          <Stat label="ttl" value={details.ping.ttl === null ? '—' : String(details.ping.ttl)} />
          <Stat label="loss" value={details.ping.packet_loss === null ? '—' : `${details.ping.packet_loss}%`} />
        </dl>
      )}

      {details.heartbeat && (
        <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1">
          <Row label="Last ping" value={details.heartbeat.last_ping_at ? formatDateTime(details.heartbeat.last_ping_at) : 'never'} />
          <Row label="Deadline" value={details.heartbeat.deadline_at ? formatDateTime(details.heartbeat.deadline_at) : '—'} />
          {details.heartbeat.late_by_seconds !== null && <Row label="Late by" value={`${details.heartbeat.late_by_seconds}s`} />}
          {details.heartbeat.duration_ms !== null && <Row label="Last run took" value={ms(details.heartbeat.duration_ms)} />}
        </dl>
      )}

      {details.children && (
        <section>
          <h4 className="font-medium mb-1">Children</h4>
          <ul className="space-y-0.5">
            {details.children.map(c => (
              <li key={c.id} className="flex items-center gap-2">
                <span className={`inline-block w-2 h-2 rounded-full ${dot(c.status)}`} aria-hidden="true" />
                <span>{c.name}</span>
                <span className="text-gray-500 dark:text-gray-400">{c.status}</span>
              </li>
            ))}
          </ul>
        </section>
      )}

      {details.body_snippet && (
        <section>
          <h4 className="font-medium mb-1">
            Response body
            {details.content_type && <span className="font-normal text-gray-500 dark:text-gray-400"> · {details.content_type}</span>}
            {details.body_bytes !== null && details.body_bytes !== undefined && (
              <span className="font-normal text-gray-500 dark:text-gray-400"> · {formatBytes(details.body_bytes)}{details.body_truncated ? ' (read capped)' : ''}</span>
            )}
          </h4>
          <pre className="font-mono text-xs p-2 bg-gray-50 dark:bg-gray-900 rounded whitespace-pre-wrap break-all max-h-48 overflow-y-auto">{details.body_snippet}</pre>
        </section>
      )}

      {details.error_code && !details.assertions?.length && (
        <p className="font-mono text-xs text-gray-500 dark:text-gray-400">code {details.error_code}</p>
      )}
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="text-[0.65rem] uppercase tracking-wide text-gray-500 dark:text-gray-400">{label}</dt>
      <dd>{value}</dd>
    </div>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex gap-2 min-w-0">
      <dt className="text-gray-500 dark:text-gray-400 shrink-0">{label}</dt>
      <dd className="break-all">{value}</dd>
    </div>
  );
}

function ms(v: number | null | undefined): string {
  if (v === null || v === undefined) return '—';
  if (v >= 1000) return `${(v / 1000).toFixed(2)} s`;
  return `${Math.round(v * 100) / 100} ms`;
}

function daysWord(d: number | null): string {
  if (d === null) return 'unknown';
  if (d < 0) return `expired ${-d} day${d === -1 ? '' : 's'} ago`;
  if (d === 0) return 'expires today';
  return `${d} day${d === 1 ? '' : 's'} left`;
}

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

function dot(status: string): string {
  switch (status) {
    case 'up': return 'bg-status-up';
    case 'down': return 'bg-status-down';
    case 'degraded': return 'bg-status-degraded';
    default: return 'bg-gray-400';
  }
}

/** One line for the test banner: what the check concluded for its type. */
export function summariseTest(type: ServiceType, r: { ok: boolean; status: string; status_code: number | null; response_time: number | null; error_message: string | null }): string {
  const t = r.response_time !== null ? `${Math.round(r.response_time)} ms` : null;
  if (r.ok) {
    switch (type) {
      case 'http': return `Responded ${r.status_code}${t ? ` in ${t}` : ''} — every assertion passed.`;
      case 'tcp': return `Port is open${t ? ` (connected in ${t})` : ''}.`;
      case 'dns': return `Resolved${t ? ` in ${t}` : ''}.`;
      case 'ping': return `Reply received${t ? ` in ${t}` : ''}.`;
      case 'heartbeat': return 'Settings are valid; the deadline below is what a ping would set.';
      case 'group': return 'All children are up.';
    }
  }
  if (r.status === 'degraded') return `Degraded: ${r.error_message ?? 'slower than the threshold'}.`;
  if (r.status === 'pending') return r.error_message ?? 'Nothing to evaluate yet.';
  return r.error_message ?? 'The check failed.';
}
